import { Form, InputNumber, Modal, Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, {ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import { loanDrawdownGetAction, loanDrawdownQsReceivedAction } from '~Loans/actions';
import { loanDrawdownSelector } from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { IDictionary } from '~utilities/IDictionary';

export default function DrawdownQsReceivedModal(props: {
    loanDrawdownUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { loanDrawdownUuid, isOpen, onCancel } = props;

    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));

    const [ dateReportReceived, setDateReportReceived ] = useState<string>(null);
    const [ qsCertifiedAmount, setQsCertifiedAmount ] = useState<number>(null);
    const [ qsCompleteAmount, setQsCompleteAmount ] = useState<number>(null);
    const [ errors, setErrors] = useState<IDictionary<string>>({});

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!drawdown) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        drawdown,
        dispatch,
        loanDrawdownUuid,
    ]);

    const validateDateReportReceived: () => boolean = useCallback(() => {
        let error: string = null;

        if (!dateReportReceived) {
            error = 'Please enter the date the QS was recieved';
        }

        setErrors((previousErrors: IDictionary<string>) => ({
            ...previousErrors,
            dateReportReceived: error,
        }));

        return !error;
    }, [dateReportReceived]);

    const validateQsCertifiedAmount: () => boolean = useCallback(() => {
        let error: string = null;

        if (qsCertifiedAmount === null) {
            error = 'Please enter the QS certified amount';
        }

        setErrors((previousErrors: IDictionary<string>) => ({
            ...previousErrors,
            qsCertifiedAmount: error,
        }));

        return !error;
    }, [qsCertifiedAmount]);

    const validateQsCompleteAmount: () => boolean = useCallback(() => {
        let error: string = null;

        if (qsCompleteAmount === null) {
            error = 'Please enter the QS cost to complete amount';
        }

        setErrors((previousErrors: IDictionary<string>) => ({
            ...previousErrors,
            qsCompleteAmount: error,
        }));

        return !error;
    }, [qsCompleteAmount]);

    const disabledTime: (time: Dayjs) => boolean = useCallback((time: Dayjs) => {
        return time && (time.isAfter(dayjs(), 'second') || time.isBefore(dayjs(drawdown.qsInstructedTime)));
    }, [drawdown.qsInstructedTime]);

    const onClickOk: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateDateReportReceived() && valid;
        valid = validateQsCertifiedAmount() && valid;
        valid = validateQsCompleteAmount() && valid;

        if (!valid) {
            return;
        }

        dispatch(loanDrawdownQsReceivedAction(drawdown.uuid, dateReportReceived, qsCertifiedAmount, qsCompleteAmount));
        onCancel();
    }, [
        validateDateReportReceived,
        validateQsCertifiedAmount,
        validateQsCompleteAmount,
        dispatch,
        drawdown.uuid,
        dateReportReceived,
        qsCertifiedAmount,
        qsCompleteAmount,
        onCancel,
    ]);

    const onChangeDateReportReceived: (date: Dayjs) => void = useCallback((date: Dayjs) => {
        setDateReportReceived(date ? dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(dateReportReceived || undefined).format('HH:mm')}`).format() : null);
    }, [dateReportReceived]);

    const onChangeTimeReportReceived: (time: Dayjs) => void = useCallback((time: Dayjs) => {
        setDateReportReceived(time ? dayjs(`${dayjs(dateReportReceived || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format() : null);
    }, [dateReportReceived]);

    const onChangeQsCertifiedAmount: (qsCertifiedAmount: number) => void = useCallback((qsCertifiedAmount: number) => {
        setQsCertifiedAmount(qsCertifiedAmount);
    }, []);

    const onChangeQsCompleteAmount: (qsCompleteAmount: number) => void = useCallback((qsCompleteAmount: number) => {
        setQsCompleteAmount(qsCompleteAmount);
    }, []);

    if (!drawdown) {
        return (
            <Modal
                footer={false}
                onCancel={onCancel}
                open={isOpen}
                title='QS Report Received'
                wrapClassName='loan-drawdown-qs-received-modal'
                width={700}
            >
                <Spin/>
            </Modal>
        );
    }

    return (
        <Modal
            okText='QS Received'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='QS Report Received'
            wrapClassName='loan-drawdown-qs-received-modal'
            width={700}
        >
            <Form.Item
                label='Date Report Received'
                className='date-report-recieved'
                help={errors.dateReportReceived}
                validateStatus={errors.dateReportReceived && 'error'}
            >
                <Space>
                    <DatePicker
                        className='date-report-recieved-pick-date'
                        disabledDate={disabledTime}
                        onChange={onChangeDateReportReceived}
                        format='DD/MM/YYYY'
                        value={dateReportReceived ? dayjs(dateReportReceived) : null}
                    />
                    <TimePicker
                        className='date-report-recieved-pick-time'
                        disabledDate={disabledTime}
                        onChange={onChangeTimeReportReceived}
                        value={dateReportReceived ? dayjs(dateReportReceived) : null}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
            <Form.Item
                label='QS Certified Amount'
                help={errors.qsCertifiedAmount}
                validateStatus={errors.qsCertifiedAmount && 'error'}
            >
                <InputNumber addonBefore='$' min={0} onChange={onChangeQsCertifiedAmount} type='number' value={qsCertifiedAmount} />
            </Form.Item>
            <Form.Item
                label='QS Cost to Complete Amount'
                help={errors.qsCompleteAmount}
                validateStatus={errors.qsCompleteAmount && 'error'}
            >
                <InputNumber addonBefore='$' min={0} onChange={onChangeQsCompleteAmount} type='number' value={qsCompleteAmount} />
            </Form.Item>
        </Modal>
    );
}